import React, { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { FileBadge, Loader2, X, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full h-10 px-2 border border-stone-300 text-sm";
const cell = "h-9 px-2 border border-stone-300 text-sm w-full";

export default function OperatorIdImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [op, setOp] = useState(null);
  const [sites, setSites] = useState([]);
  const [srcDoc, setSrcDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => { setFile(null); setErr(""); setOp(null); setSites([]); setSrcDoc(null); };

  const scan = async () => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/compliance/scan-operator-id", fd, { timeout: 120000 });
      const o = data.operator || {};
      setOp({ operator_name: o.operator_name || "", operator_id_number: o.operator_id_number || "", state: o.state || "CA" });
      setSrcDoc(data.source_document || null);
      setSites((data.sites || []).map((s) => ({
        include: true, site_name: s.site_name || "", site_identifier: s.site_identifier || "",
        county: s.county || o.county || "", acres: s.acres ?? "", commodity: s.commodity || "",
        variety: s.variety || "", location: s.location || "",
        confident: s.confident !== false, match: s.match || null,
      })));
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed — try again"); }
    finally { setBusy(false); }
  };

  const setSite = (i, upd) => setSites((s) => s.map((x, k) => (k === i ? { ...x, ...upd } : x)));

  const confirm = async () => {
    const chosen = sites.filter((s) => s.include && s.site_name.trim());
    if (!chosen.length) { setErr("Select at least one site"); return; }
    setSaving(true); setErr("");
    try {
      const { data } = await api.post("/compliance/operator-id/confirm", {
        operator_name: op.operator_name || null, operator_id_number: op.operator_id_number || null,
        state: op.state || null, source_document: srcDoc,
        sites: chosen.map((s) => ({
          site_name: s.site_name.trim(), site_identifier: s.site_identifier || null,
          county: s.county || null, acres: s.acres !== "" ? Number(s.acres) : null,
          commodity: s.commodity || null, variety: s.variety || null,
          location: s.location || null, match_id: s.match?.id || null,
        })),
      });
      toast.success(`${data.created} propert${data.created === 1 ? "y" : "ies"} created · ${data.updated} updated`);
      setOpen(false); reset();
      onDone && onDone();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button data-testid="import-opid-btn" onClick={() => { reset(); setOpen(true); }}
        className="h-14 px-5 bg-white text-[#1F3D34] font-bold uppercase border border-stone-200 shadow-sm flex items-center gap-2">
        <FileBadge size={16} /> Operator ID Form
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-white w-full max-w-4xl p-5 space-y-4 my-8" data-testid="opid-import-modal">
            <div className="flex items-center justify-between">
              <div className="font-bold uppercase tracking-wide flex items-center gap-2"><Sparkles size={16} className="text-[#B08D3E]" /> Upload County Operator ID Form</div>
              <button data-testid="opid-close" onClick={() => setOpen(false)} className="h-9 w-9 border border-stone-300 flex items-center justify-center"><X size={15} /></button>
            </div>

            {!op && (
              <div className="space-y-3">
                <p className="text-sm text-stone-500">Upload your County Ag Commissioner Operator ID / Restricted Materials Permit (PDF or photo). The AI extracts the operator number and every registered site — you review and correct everything before any property is saved. Low-confidence rows are flagged.</p>
                <input data-testid="opid-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
                {err && <div className="bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="opid-error">{err}</div>}
                <button data-testid="opid-scan-btn" onClick={scan} disabled={!file || busy}
                  className="h-12 px-6 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40 flex items-center gap-2">
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Reading form…</> : "Scan Form"}
                </button>
              </div>
            )}

            {op && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[["operator_name", "Operator / Grower"], ["operator_id_number", "Operator ID #"], ["state", "State"]].map(([k, label]) => (
                    <div key={k}>
                      <div className="text-[9px] font-bold uppercase text-stone-400">{label}</div>
                      <input data-testid={`opid-${k}`} value={op[k]} onChange={(e) => setOp({ ...op, [k]: e.target.value })} className={inp} />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-stone-500 mb-2">Registered Sites ({sites.filter((s) => s.include).length} selected) — existing properties matched by site + county update instead of duplicating</div>
                  <div className="overflow-x-auto border border-stone-200">
                    <table className="w-full text-sm min-w-[760px]">
                      <thead><tr className="bg-stone-100 text-left text-[10px] uppercase">
                        <th className="p-2"></th><th className="p-2">Site Name</th><th className="p-2">Site ID / APN</th>
                        <th className="p-2">County</th><th className="p-2">Acres</th><th className="p-2">Commodity</th><th className="p-2">Action</th>
                      </tr></thead>
                      <tbody>
                        {sites.map((s, i) => (
                          <tr key={i} className={`border-t border-stone-200 ${s.include ? "" : "opacity-40"} ${!s.confident ? "bg-amber-50" : ""}`} data-testid={`opid-row-${i}`}>
                            <td className="p-2"><input data-testid={`opid-include-${i}`} type="checkbox" checked={s.include} onChange={(e) => setSite(i, { include: e.target.checked })} className="w-4 h-4" /></td>
                            <td className="p-1">
                              <div className="flex items-center gap-1">
                                {!s.confident && <AlertTriangle size={13} className="text-amber-600 shrink-0" data-testid={`opid-flag-${i}`} />}
                                <input data-testid={`opid-site-${i}`} value={s.site_name} onChange={(e) => setSite(i, { site_name: e.target.value })} className={cell} />
                              </div>
                            </td>
                            <td className="p-1 w-28"><input value={s.site_identifier} onChange={(e) => setSite(i, { site_identifier: e.target.value })} className={cell} /></td>
                            <td className="p-1 w-28"><input value={s.county} onChange={(e) => setSite(i, { county: e.target.value })} className={cell} /></td>
                            <td className="p-1 w-20"><input type="number" step="0.1" value={s.acres} onChange={(e) => setSite(i, { acres: e.target.value })} className={cell} /></td>
                            <td className="p-1 w-28"><input value={s.commodity} onChange={(e) => setSite(i, { commodity: e.target.value })} className={cell} /></td>
                            <td className="p-2">
                              {s.match
                                ? <span className="text-[10px] font-bold uppercase px-2 py-1 bg-sky-100 text-sky-800" data-testid={`opid-match-${i}`}>Update {s.match.ranch_number}</span>
                                : <span className="text-[10px] font-bold uppercase px-2 py-1 bg-[#E8EFE6] text-[#1F3D34]">New</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {err && <div className="bg-red-50 border border-red-400 text-red-800 p-3 text-sm font-semibold" data-testid="opid-error">{err}</div>}
                <div className="flex gap-2">
                  <button data-testid="opid-restart" onClick={reset} className="h-12 px-4 border border-stone-300 font-bold uppercase text-xs">Start Over</button>
                  <button data-testid="opid-confirm-btn" onClick={confirm} disabled={saving}
                    className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40 flex items-center justify-center gap-2">
                    {saving && <Loader2 size={14} className="animate-spin" />} Confirm & Save {sites.filter((s) => s.include).length} Site{sites.filter((s) => s.include).length === 1 ? "" : "s"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
